import React, {useEffect, useRef} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {FormControl, Table} from "react-bootstrap";

import {RootProgramFieldEditor} from "../../../Models/Core/FieldEditors";
import {ProjectFieldEditor} from "../../../Models/Core/ServiceDrivenEditors";
import {service as ReportService} from "../../../Services/ReportService";

import {
    SET_MONITORING_EVENT_ID,
    SET_PHASE_ID,
    SET_PHASES,
    SET_PROCEDURE_ID,
    SET_PROCEDURES,
    SET_PROGRAM_ID,
    SET_PROGRAM_YEAR,
    SET_PROGRAM_YEARS,
    SET_PROJECT_ID,
    SET_REPORT_TYPE_ID,
    SET_REPORT_TYPES,
    SET_SECTIONS,
} from "../../../Redux/Actions/Types/reportActionTypes";
import {ReportSelectComponent} from "./ReportSelectComponent";


export const ParameterComponent = props => {
    const {reportTypes, phases, programYears, procedures, reportTypeId, programId, projectId, phaseId, programYear, procedureId, monitoringEventId} = useSelector(state => state.reportState);

    const dispatch = useDispatch();

    const reportCode = reportTypes?.find((type) => String(type.reportTypeId) === String(reportTypeId))?.code?.toLowerCase();
    
    const procedure = procedures?.find( p => String(p.procedureId) === String(procedureId) );
    
    let mostRecentRequestPromise = useRef(null);

    const showContext = (prefixes, exclude = []) => {
        if (!reportCode) {
            return false;
        }

        if(!(prefixes instanceof Array)) {
            prefixes = [prefixes];
        }
        
        if(exclude.some(excludedCode => reportCode === excludedCode)) {
            return false;
        }

        return prefixes.some( prefix => reportCode.startsWith(prefix) );
    };

    const showProgramYear = (programId) && showContext('program');
    const showPhase = (projectId || programId) && showContext('project', ['project-validation-checklist']); // || sampleEventId;
    const showProcedure = projectId && showContext('monitoring-event');
    const showMonitoringEvent = projectId && procedureId && showContext('monitoring-event');
    
    // clear phaseId when phase is not applicable
    // to prevent filtering of monitoring events by phaseId
    // previous set from annual report parameters
    useEffect(() => {
        if (!showPhase && phaseId !== null) {
            dispatch({type: SET_PHASE_ID, phaseId: null});
        }
    }, [reportCode, showPhase, phaseId]);
    
    useEffect(() => {
        const thisPromise = mostRecentRequestPromise.current = ReportService.Get(programId, projectId, phaseId).then((x) => {
            console.log(x.result);
            if(thisPromise !== mostRecentRequestPromise.current) {
                console.log('skipped', this, mostRecentRequestPromise.current);
                return;
            }

            // for now the only Monitoring Event Report we support is Photo Monitoring
            const supportedProcedures = (x.result.Procedures ?? []).filter( p => p.code === 'photo');
            
            dispatch({type: SET_REPORT_TYPES, reportTypes: x.result.ReportTypes ?? []});
            dispatch({type: SET_PHASES, phases: x.result.SampleEventPhases ?? []});
            dispatch({type: SET_PROGRAM_YEARS, programYears: x.result.ProgramYears ?? []});
            dispatch({type: SET_SECTIONS, sections: x.result.ReportSections ?? []});
            dispatch({type: SET_PROCEDURES, procedures: supportedProcedures ?? []});
        });
    }, [reportTypeId, programId, projectId, phaseId]);

    return <div>
        <h3 className="table-headers">Main Parameters</h3>
        <Table striped bordered size="sm" className="sb-model-viewer">
            <tbody>
                <tr>
                    <td className="sb-model-viewer-column"><label>Report Type</label></td>
                    <td className="sb-model-viewer-column">
                        <FormControl
                            as="select"
                            value={reportTypeId}
                            onChange={(x) => dispatch({type: SET_REPORT_TYPE_ID, reportTypeId: x.target.value})}
                        >
                            <option value="">Select Report Type</option>
                            {
                                reportTypes?.map((type) => (
                                    <option key={type.reportTypeId} value={type.reportTypeId} disabled={type.disabled}>
                                        {type.name}
                                    </option>
                                ))
                            }
                        </FormControl>
                    </td>
                </tr>
                {
                    showContext('program') &&
                    <tr>
                        <td className="sb-model-viewer-column"><label>Program</label></td>
                        <td className="sb-model-viewer-column">
                            <RootProgramFieldEditor
                                Value={programId}
                                OnUpdate={(value) => dispatch({type: SET_PROGRAM_ID, programId: value})}
                                filter={(values, selectedItem) => {
                                    return values.filter(x => x.slug === 'srsp');
                                }}
                                emptyMessage={"No supported programs found."}
                            />
                        </td>
                    </tr>
                }
                {
                    (showContext('project') || showContext('monitoring-event')) &&
                    <tr>
                        <td className="sb-model-viewer-column"><label>Project</label></td>
                        <td className="sb-model-viewer-column">
                            <ProjectFieldEditor
                                Value={projectId}
                                OnUpdate={(value) => dispatch({type: SET_PROJECT_ID, projectId: value})}
                                additionalSearchParams={{
                                    onlyWithMonitoringEvents: !showContext('project'),
                                }}
                            />
                        </td>
                    </tr>
                }
                {
                    showPhase &&
                    <ReportSelectComponent
                        title="Phase"
                        primaryKey="sampleEventPhaseId"
                        data={phases}
                        value={phaseId}
                        onChange={(value) => dispatch({type: SET_PHASE_ID, phaseId: value})}
                        placeholder="Select Phase"
                        emptyMessage="This Project has no Monitoring Events."
                    />
                }
                {
                    showProgramYear &&
                    <ReportSelectComponent
                        title="Program Year"
                        primaryKey="programYear"
                        data={programYears}
                        value={programYear}
                        onChange={(value) => dispatch({type: SET_PROGRAM_YEAR, programYear: value})}
                        placeholder="Select Program Year"
                        emptyMessage="This Program has no modeled uplift"
                    />
                }
                {
                    !!showProcedure &&
                    <ReportSelectComponent
                        title="Procedure"
                        primaryKey="procedureId"
                        data={procedures}
                        value={procedureId}
                        onChange={(value) => dispatch({type: SET_PROCEDURE_ID, procedureId: value})}
                        placeholder="Select Procedure"
                        emptyMessage="No procedures available."
                    />
                }
                {
                    !!showMonitoringEvent &&
                    <ReportSelectComponent
                        title="Monitoring Event"
                        primaryKey="sampleEventId"
                        nameLogic={ (item) => item.projectPhaseName + ' - ' + item.shortName }
                        data={procedure?.SampleEvents ?? []}
                        value={monitoringEventId}
                        onChange={(value) => dispatch({type: SET_MONITORING_EVENT_ID, monitoringEventId: value})}
                        placeholder="Select Monitoring Event"
                        emptyMessage="No monitoring events available."
                    />
                }
            </tbody>
        </Table>
    </div>;
};


